/**
 * Multi-currency price catalog.
 *
 * Monthly price points supplied by the account owner 08/14/2026, replacing the derived figures
 * in README.md — the README's own note said those were "derived, not confirmed" and must not
 * ship.
 *
 * Each monthly figure is a FIXED CATALOG PRICE POINT for its currency. They are not converted
 * from USD and must never float with an exchange rate.
 *
 * **The annual price is computed, not stored:** `monthly × 12`, less 15%. Confirmed by the
 * account owner 08/14/2026 as the rule for every currency. Storing annual figures separately
 * is what produced four contradictory price points in the first pass — with one formula there
 * is nothing left to disagree with itself, and the "Save 15%" claim on the pricing page is
 * true by construction.
 *
 * Rounding is DOWN, to whole currency units. Down rather than nearest so the computed price
 * can never exceed the formula the customer was shown.
 */

import type { Tier } from '@/lib/tier';

export type CurrencyCode = 'USD' | 'CAD' | 'GBP' | 'AUD' | 'NZD';

export interface Currency {
  code: CurrencyCode;
  symbol: string;
  /** Regional flag shown in the selector (README § 11). */
  flag: string;
  label: string;
}

export const CURRENCIES: Record<CurrencyCode, Currency> = {
  USD: { code: 'USD', symbol: '$', flag: '🇺🇸', label: 'US Dollar' },
  CAD: { code: 'CAD', symbol: 'C$', flag: '🇨🇦', label: 'Canadian Dollar' },
  GBP: { code: 'GBP', symbol: '£', flag: '🇬🇧', label: 'Pound Sterling' },
  AUD: { code: 'AUD', symbol: 'A$', flag: '🇦🇺', label: 'Australian Dollar' },
  NZD: { code: 'NZD', symbol: 'NZ$', flag: '🇳🇿', label: 'New Zealand Dollar' },
};

/** The annual prepay discount, applied identically in every currency. */
export const ANNUAL_DISCOUNT = 0.15;

/**
 * Snapshot is sold only in USD and CAD. The plan is not offered outside the US and Canada, so
 * in any other currency its card goes to an "unavailable in this currency" state with a
 * "See Intelligence instead" CTA — it is NOT hidden and NOT priced (README § 11).
 *
 * Snapshot's GBP, AUD and NZD monthly figures were supplied and are retained below because
 * the Paddle catalog may carry them, but `SOLD_IN` is what the UI obeys.
 */
export const SOLD_IN: Record<Tier, readonly CurrencyCode[]> = {
  snapshot: ['USD', 'CAD'],
  intelligence: ['USD', 'CAD', 'GBP', 'AUD', 'NZD'],
  pro: ['USD', 'CAD', 'GBP', 'AUD', 'NZD'],
};

/**
 * Monthly price points. The only prices stored anywhere.
 *
 * Confirmed with the account owner 2026-08-15. **Three tiers, all single-seat.**
 *
 * Do not reconcile this table with awarenow.ai/pricing. That page is the CURRENT PLATFORM and
 * differs deliberately: it still advertises a fourth tier, **Insight** at $29, which has been
 * withdrawn; and it prices Intelligence at $69, which is the current platform's figure, not
 * this product's. **Intelligence is $99 here.** Intelligence Pro was formerly "5 users" at the
 * same $199 and is now single-seat, so there is no seat model anywhere in this codebase.
 *
 * Snapshot is not a whole number in GBP (£8.95) and NZD (NZ$20.50). That is why `formatPrice`
 * shows two decimals for a non-integer amount, and why these are not rounded to look tidier.
 *
 * TWO CELLS WERE CORRECTED against the originally supplied table, on evidence rather than
 * preference — **Snapshot CAD 17 → 16**, and **Pro GBP 159 → 149**:
 *
 *   - Paddle's own annual prices for those two are 163.20 and 1519.80, which are exactly 15%
 *     off twelve months of 16.00 and 149.00 — and off nothing else.
 *   - The currency table published on awarenow.ai/pricing independently gives 16 and 149.
 *
 * With those two values, every annual price already in the Paddle catalog reproduces exactly.
 * With 17 and 159 none of the four do. Paddle's *monthly* figures for these two are also wrong
 * (16.50 and 159.00) and overcharge — see the note at the foot of `ops/11-seed-price-map.sql`.
 *
 * `intelligence` HAS NO PADDLE PRODUCT AT ALL. The card renders from the figures below, but
 * nothing can be bought at these prices until the product and its ten prices exist.
 */
export const MONTHLY: Record<CurrencyCode, Record<Tier, number>> = {
  USD: { snapshot: 12, intelligence: 99, pro: 199 },
  CAD: { snapshot: 16, intelligence: 137, pro: 278 },
  GBP: { snapshot: 8.95, intelligence: 73, pro: 149 },
  AUD: { snapshot: 18, intelligence: 139, pro: 305 },
  NZD: { snapshot: 20.5, intelligence: 168, pro: 345 },
};

export interface PricePoint {
  monthly: number;
  /** Charged once per year. Derived — see the note at the top of this file. */
  annual: number;
}

export function monthlyPrice(currency: CurrencyCode, tier: Tier): number {
  return MONTHLY[currency][tier];
}

/**
 * `monthly × 12`, less 15%.
 *
 * Rounded to the cent, NOT down to a whole unit. Rounding down was wrong: the catalog's annual
 * prices carry cents — $122.40, £91.29, CA$2,835.60 — so flooring displayed $122 against a
 * $122.40 charge.
 *
 * The 2dp rounding is also load-bearing arithmetic, not cosmetic: `12 * 12 * 0.85` evaluates
 * to 122.39999999999999 in binary floating point, which formats as "$122.40" only by luck of
 * the formatter. Rounding makes it true rather than merely well-presented.
 *
 * With the monthly figures above, this reproduces **all ten** annual prices already in the
 * Paddle catalog exactly — the check that identified two wrong monthly prices in Paddle rather
 * than the other way round. Every annual in the catalog was right; two monthlies were not.
 * If a future annual stops matching, suspect the monthly first.
 */
export function annualPrice(currency: CurrencyCode, tier: Tier): number {
  const raw = monthlyPrice(currency, tier) * 12 * (1 - ANNUAL_DISCOUNT);
  return Math.round(raw * 100) / 100;
}

export function priceFor(currency: CurrencyCode, tier: Tier): PricePoint {
  return { monthly: monthlyPrice(currency, tier), annual: annualPrice(currency, tier) };
}

export function isSoldIn(tier: Tier, currency: CurrencyCode): boolean {
  return SOLD_IN[tier].includes(currency);
}

/**
 * The real prepay saving, measured from the two figures rather than assumed.
 *
 * With the annual price derived this always lands at or just above 15% — rounding down can
 * only ever deepen the discount. Measuring it anyway costs nothing and means the badge stays
 * honest if these prices are ever moved into the Paddle catalog and read from there instead.
 */
export function annualSavingPct(currency: CurrencyCode, tier: Tier): number {
  const { monthly, annual } = priceFor(currency, tier);
  return Math.floor((1 - annual / (monthly * 12)) * 1000) / 10;
}

/** Cash saved by prepaying, in whole currency units. */
export function annualSavingAmount(currency: CurrencyCode, tier: Tier): number {
  const { monthly, annual } = priceFor(currency, tier);
  return monthly * 12 - annual;
}

/**
 * The per-month figure shown on an annual card. Presentational only — the customer is charged
 * `annual` once, and the card must also show that real total (README § 11).
 */
export function annualPerMonth(currency: CurrencyCode, tier: Tier): number {
  return annualPrice(currency, tier) / 12;
}

/** US number and currency formatting throughout: $1,234.56 (BUSINESS_RULES § Locale rule 3). */
export function formatPrice(
  currency: CurrencyCode,
  amount: number,
  { cents = false }: { cents?: boolean } = {},
): string {
  const digits = cents || !Number.isInteger(amount) ? 2 : 0;
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${CURRENCIES[currency].symbol}${formatted}`;
}
